// ./src/utils/reportData.js
import { getTermDays } from "./reportRecords.js";
import { getDatasetTypes } from "./reportConfigs.js";
import { calcOptionTotal } from "./calcOptionTotal";

// const intervalDaily = 0;
const intervalWeekly = 1; // interval length from Controls component
const intervalMonthly = 2;
// const intervalQuarterly = 3;

// Calculate the total value of the job associated with each record
const calcValue = (crntJob, crntOption, reports, prices) => {
	if (!crntJob || !crntOption) {
		console.log("crntJob:", crntJob);
		console.log("crntOption:", crntOption);
		return;
	}
	const jobItems = reports?.jobItems.filter((item) => item.options_id === crntOption.id);
	const fees = reports?.fees.filter((fee) => fee.options_id === crntOption.id);
	const discounts = reports?.discounts.filter((dsct) => dsct.options_id === crntOption.id);

	const totals = calcOptionTotal({
		jobItems,
		discounts,
		fees,
		sales_tax: crntOption.sales_tax,
		job: crntJob,
		prices,
	});

	// console.log("totals:", totals.grandTotal);
	return totals.grandTotal;
};

// Track largest job
const updateLargestJob = (val, currentJob, largestJob) => {
	// If current job is largest return as new largest job
	if (val > largestJob.amount) {
		const amount = val;
		const jobId = currentJob.id;
		const jobNumber = currentJob.job_number;
		largestJob = { amount, jobId, jobNumber };
		console.log("new largestJob:", largestJob);
	}
	// Otherwise return current largest job
	return largestJob;
};

const calculateDayValue = (records, reports, prices) => {
	let dayQty = 0;
	let largestJob = {};

	const dayVal = records.reduce((ttl, currentAppt) => {
		const currentJob = reports?.jobs?.filter((job) => job.id === currentAppt.core_job_data_id)[0];
		const currentOptions = reports?.options?.filter(
			(opt) => opt.core_job_data_id === currentAppt.core_job_data_id
		);
		const chosenOption = currentOptions.filter((opt) => opt.id === currentJob.options_id)[0];

		// Determine value for estimates with multiple options
		if (currentOptions.length > 1) {
			// If chosen option not present then take an average value
			if (!chosenOption) {
				let ttlVal = 0;
				let qty = 0; // keep track to remove custom price estimates
				currentOptions.forEach((opt) => {
					const val = calcValue(currentJob, opt, reports, prices);
					largestJob = updateLargestJob(val, currentJob, largestJob);
					if (val) {
						qty = qty + 1;
						ttlVal = ttlVal + val;
					}
				});
				// If none of the estimates have a valid price
				if (qty === 0) {
					console.log(
						`Not counting this multi-option appointment (${currentAppt.id})! Daily quantity: `,
						dayQty
					);
					return ttl; // don't change the total value
				}
				dayQty = dayQty + 1;
				return ttl + ttlVal / qty;
			}
		}
		// Otherwise use single value for daily total
		const val = calcValue(currentJob, chosenOption || currentOptions[0], reports, prices);
		largestJob = updateLargestJob(val, currentJob, largestJob);
		// Don't count if custom price
		if (!val) {
			console.log(`Not counting this appointment (${currentAppt.id})! Daily quantity: `, dayQty);
			return ttl; // don't change the total value
		}

		dayQty = dayQty + 1;
		return ttl + val;
	}, 0);

	return { dayQty, dayVal, largestJob };
};

export const processAllDatasets = (allRecords, reports, prices, lists, yearsOffset = 0) => {
	// Define all your categories (base, velux, lead source A, B, etc.)
	const datasetTypes = getDatasetTypes(({ reports, lists }))

	// Step 1: Group ALL records by day AND category (single pass)
	const dayCategoryMap = {};
	allRecords.forEach((record) => {
		const day = new Date(record.appt_date || record.payment_date).toISOString().split("T")[0];
		if (!dayCategoryMap[day]) dayCategoryMap[day] = {};

		datasetTypes.forEach((cat) => {
			if (cat.filter(record)) {
				if (!dayCategoryMap[day][cat.name]) dayCategoryMap[day][cat.name] = [];
				dayCategoryMap[day][cat.name].push(record);
			}
		});
	});

	// Step 2: Process date range once for all dataset types
	const termDays = getTermDays(yearsOffset, reports);
	const results = {};
	const aggState = {};

	// Initialize
	datasetTypes.forEach((cat) => {
		results[cat.name] = { labels: [], quantity: [], value: [], largestJob: { amount: 0 } };
		aggState[cat.name] = { termQty: 0, termVal: 0, termNumber: null };
	});

	termDays.forEach((day, i) => {
		const date = new Date(day);
		const formattedDay = day.split("T")[0].split(" ")[0];
		const fullDateString = date.toDateString().slice(0, -5);
		const monthlyDateString = date.toDateString().slice(4, -5);

		datasetTypes.forEach((cat) => {
			const state = aggState[cat.name];
			let termsLargestJob = results[cat.name].largestJob;
			const dayRecords = dayCategoryMap[formattedDay]?.[cat.name] || [];
			const { dayVal, dayQty, largestJob } = calculateDayValue(dayRecords, reports, prices);
			// Check and update largest job as needed
			results[cat.name].largestJob =
				largestJob.amount > termsLargestJob.amount ? largestJob : termsLargestJob;

			// Compile data into monthly data points if desired
			if (reports.interval === intervalMonthly) {
				// Start tracking for first month
				if (i === 0) {
					results[cat.name].labels.push(monthlyDateString);
					state.termNumber = date.getMonth();
					// If month starts over
				} else if (date.getMonth() !== state.termNumber) {
					results[cat.name].labels.push(monthlyDateString);
					results[cat.name].quantity.push(state.termQty);
					results[cat.name].value.push(state.termVal);
					state.termQty = 0;
					state.termVal = 0;
					state.termNumber = date.getMonth();
					// Add data from rest of last month
				} else if (i === termDays.length - 1) {
					results[cat.name].quantity.push(state.termQty);
					results[cat.name].value.push(state.termVal);
				}
				state.termQty += dayQty;
				state.termVal += dayVal;
			}
			// Or compile data into weekly data points if desired
			else if (reports.interval === intervalWeekly) {
				// Start tracking for first week
				if (i === 0) {
					results[cat.name].labels.push(monthlyDateString);
					state.termNumber = date.getMonth();
				}
				// If week starts over
				else if (i % 7 === 0) {
					results[cat.name].labels.push(fullDateString);
					results[cat.name].quantity.push(state.termQty);
					results[cat.name].value.push(state.termVal);
					state.termQty = 0;
					state.termVal = 0;
				}
				// Add data from rest of last week
				else if (i === termDays.length - 1) {
					results[cat.name].quantity.push(state.termQty);
					results[cat.name].value.push(state.termVal);
				}
				state.termQty += dayQty;
				state.termVal += dayVal;
			}
			// Otherwise just push daily values
			else {
				results[cat.name].labels.push(fullDateString);
				results[cat.name].quantity.push(dayQty);
				results[cat.name].value.push(dayVal);
			}
		});
	});

	return results;
};

